import React, { useState } from 'react';
import { varAlpha } from '../utils/statusColors';
import { Link } from 'react-router-dom';
import { useBotProtection } from '../hooks/useBotProtection';
import { KaziAtlasLogo } from '../components/KaziAtlasLogo';
import { usePageTitle } from '../hooks/usePageTitle';
import { useTheme } from '../hooks/useTheme';
import { trackAnalyticsEvent } from '../utils/analytics';

interface RegisterForm {
    org_name: string;
    first_name: string;
    last_name: string;
    email: string;
}

export function RegisterPage() {
    usePageTitle('Create your workspace');
    const { theme } = useTheme();
    const bot = useBotProtection();
    const [sent, setSent] = useState(false);
    const [form, setForm] = useState<RegisterForm>({
        org_name: '', first_name: '', last_name: '', email: '',
    });
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);

    function update(field: keyof RegisterForm) {
        return (e: React.ChangeEvent<HTMLInputElement>) =>
            setForm(prev => ({ ...prev, [field]: e.target.value }));
    }

    async function handleSubmit(e: React.FormEvent) {
        e.preventDefault();
        setLoading(true); setError(null);
        try {
            await bot.post(
                '/auth/register',
                {
                    org_name: form.org_name,
                    first_name: form.first_name,
                    last_name: form.last_name,
                    email: form.email,
                }
            );
            setSent(true);
            trackAnalyticsEvent('signup_verification_requested');
        } catch (err: unknown) {
            const msg = (err as { response?: { data?: { message?: string } } })?.response?.data?.message
                ?? (err instanceof Error ? err.message : 'Registration failed. Please try again.');
            setError(msg);
        } finally { setLoading(false); }
    }

    return (
        <div className="login-container">
            <div className="login-card" style={{ maxWidth: 480 }}>
                <div style={{ marginBottom: '1.5rem' }}>
                    <KaziAtlasLogo size={46} onLight={theme === 'light'} />
                </div>

                <h1 className="login-title">Create your workspace</h1>
                <p className="login-subtitle">
                    New workspaces start on Starter: three active projects and three people, including you. Signing up asks for no payment details; a workspace
                    admin can change plans later from Billing.
                </p>

                {error && (
                    <div role="alert" style={{
                        padding: '0.75rem 1rem', borderRadius: 'var(--radius-sm)',
                        background: varAlpha('danger', 0.15), color: 'var(--danger)',
                        border: `1px solid ${varAlpha('danger', 0.3)}`,
                        fontSize: 'var(--font-size-sm)', marginBottom: '1.25rem',
                    }}>
                        {error}
                    </div>
                )}

                {sent ? <div role="status"><h2>Check your email</h2><p>If this email can be registered, a verification link will arrive shortly. The link expires in 30 minutes. Open it to choose your password and create your workspace.</p><p>Check your spam folder too. You can request another link after a minute.</p><button type="button" className="btn btn-secondary" onClick={() => setSent(false)}>Request another link</button></div> : <form className="login-form" onSubmit={handleSubmit} style={{ gap: '0.875rem' }}>
                    <div className="form-group">
                        <label className="form-label" htmlFor="org_name">Organization / Workspace Name</label>
                        <input
                            id="org_name" className="form-input" type="text"
                            placeholder="e.g. Acme Corp"
                            value={form.org_name} onChange={update('org_name')}
                            autoComplete="organization"
                        />
                        <span style={{ fontSize: 'var(--font-size-xs)', color: 'var(--ink-muted)' }}>
                            Leave blank to use your name
                        </span>
                    </div>

                    <div className="form-row" style={{ gap: '0.75rem' }}>
                        <div className="form-group">
                            <label className="form-label" htmlFor="first_name">First Name *</label>
                            <input id="first_name" className="form-input" type="text" required autoComplete="given-name"
                                placeholder="Jane" value={form.first_name} onChange={update('first_name')} />
                        </div>
                        <div className="form-group">
                            <label className="form-label" htmlFor="last_name">Last Name *</label>
                            <input id="last_name" className="form-input" type="text" required autoComplete="family-name"
                                placeholder="Smith" value={form.last_name} onChange={update('last_name')} />
                        </div>
                    </div>

                    <div className="form-group">
                        <label className="form-label" htmlFor="email">Work Email *</label>
                        <input id="email" className="form-input" type="email" required autoComplete="email"
                            placeholder="jane@company.com" value={form.email} onChange={update('email')} />
                    </div>

                    <button
                        id="register-submit"
                        className="btn btn-primary form-button"
                        type="submit"
                        disabled={loading}
                    >
                        {loading ? 'Sending verification link…' : 'Verify my email'}
                    </button>
                    <p style={{ color: 'var(--ink-muted)', fontSize: '0.8125rem', lineHeight: 1.6, margin: 0 }}>
                        By creating a workspace, you agree to the{' '}
                        <Link to="/terms" style={{ color: 'var(--accent-text)' }}>Terms of service</Link>.
                        {' '}Read our <Link to="/privacy" style={{ color: 'var(--accent-text)' }}>Privacy Policy</Link>.
                    </p>
                    {bot.widget}
                </form>}
            </div>

            <p style={{ textAlign: 'center', marginTop: '1.25rem', fontSize: '0.875rem', color: 'var(--ink-muted)' }}>
                Already have an account?{' '}
                <Link to="/login" style={{ color: 'var(--accent)', fontWeight: 600, textDecoration: 'none' }}>Sign in</Link>
            </p>
        </div>
    );
}
